import { useEffect, useRef } from 'react';

import { useFreeTransfers } from './useFreeTransfers';
import { useResolvedPreferences } from './useResolvedPreferences';
import { useAITransfersStore } from '@lib/transfers/store/useAITransfersStore';
import { useRecommendedTransfersStore } from '@lib/transfers/store/useRecommendedTransfersStore';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { useTransferRecommendations } from '@hooks/useTransferRecommendations';
import { describeTransferRecommendationsRefusal } from '@api-service/recommendations';

const PRE_SEASON_MAX_TRANSFERS = 3;

/**
 * Caps the solver request at what the member can actually afford, so a
 * recommendation never quietly implies a points hit (FFH-2998). Mirrors
 * scoreline-app's resolveMaxTransfers.
 *
 * Pre-season everyone gets a fixed page of three suggestions; in-season the
 * real remaining/free count is the cap; unlimited states (wildcard, free hit,
 * post-deadline server lag) leave the count to the backend so the solver can
 * rebuild the squad.
 */
const resolveMaxTransfers = (
  isPreSeason: boolean,
  isUnlimited: boolean,
  effectiveMaxTransfers: number
): number | undefined => {
  if (isPreSeason) return PRE_SEASON_MAX_TRANSFERS;
  if (isUnlimited || effectiveMaxTransfers < 1) return undefined;
  return effectiveMaxTransfers;
};

export const useRecommendedTransfers = () => {
  const {
    freeTransfers,
    remainingTransfers,
    transfersMade,
    isAllowanceKnown,
    isUnlimited,
    isReady: isFreeTransfersReady
  } = useFreeTransfers();

  const { context } = useLeagueContext();
  const isPreSeason = context?.nextGameweek?.number === 1;

  const { configuration: resolvedPreferences, isReady: isPreferencesReady } =
    useResolvedPreferences();

  const blacklistedPlayers = useAITransfersStore(
    (state) => state.blacklistedPlayers
  );
  const disabledPlayerIds = useRecommendedTransfersStore(
    (state) => state.disabledPlayerIds
  );
  const activePlayerIdsIn = useRecommendedTransfersStore(
    (state) => state.activePlayerIdsIn
  );
  const commitRegenerate = useRecommendedTransfersStore(
    (state) => state.commitRegenerate
  );
  const resetRecommendedTransfersStore = useRecommendedTransfersStore(
    (state) => state.reset
  );

  const excludePlayerIds = blacklistedPlayers
    .filter((p) => p.type === 'in')
    .map((p) => p.id)
    .join(',');

  const hasDisabledPlayers = disabledPlayerIds.length > 0;

  // Freeze the excludePlayerIds while players are mid-dismiss so the cached
  // recommendation list stays put. Updates resume once nothing is disabled.
  const stableExcludePlayerIds = useRef(excludePlayerIds);
  if (!hasDisabledPlayers) {
    stableExcludePlayerIds.current = excludePlayerIds;
  }

  const effectiveMaxTransfers = remainingTransfers ?? freeTransfers;
  const hasFreeTransfers = isUnlimited || effectiveMaxTransfers >= 1;

  const maxTransfers = resolveMaxTransfers(
    isPreSeason,
    isUnlimited,
    effectiveMaxTransfers
  );

  const {
    data: transferRecommendations,
    error,
    isLoading,
    isRefetching,
    isSuccess: isTransferRecommendationsSuccess,
    isError,
    refetch
  } = useTransferRecommendations({
    ...(maxTransfers !== undefined && { maxTransfers }),
    ...resolvedPreferences,
    ...(stableExcludePlayerIds.current && {
      excludePlayerIds: stableExcludePlayerIds.current
    }),
    ...(activePlayerIdsIn && { playerIdsIn: activePlayerIdsIn }),
    enabled:
      isFreeTransfersReady &&
      isPreferencesReady &&
      !hasDisabledPlayers &&
      hasFreeTransfers
  });

  // Pending dismissals disable the query so the frozen list stays on screen,
  // relying on that list staying cached. Committing transfers elsewhere (e.g.
  // the pick page) removes inactive recommendation queries, so returning here
  // with dismissals pending would deadlock into a permanent loading state:
  // no data to render, and no rendered list to restore or regenerate from.
  // The pending state is orphaned at that point, so drop it and run fresh.
  useEffect(() => {
    if (hasDisabledPlayers && !transferRecommendations) {
      resetRecommendedTransfersStore();
    }
  }, [
    hasDisabledPlayers,
    transferRecommendations,
    resetRecommendedTransfersStore
  ]);

  const regenerate = () => {
    commitRegenerate();
  };

  return {
    freeTransfers,
    remainingTransfers,
    transfersMade,
    isAllowanceKnown,
    isUnlimited,
    transferRecommendations,
    // A background refetch means the cached list is stale (e.g. the query was
    // invalidated by committing transfers, or a regenerate is in flight), so
    // surface it as loading rather than flashing outdated recommendations.
    isLoading: isLoading || isRefetching,
    isError,
    // Set only when the API refused the request and said why, so a service
    // failure keeps the generic copy instead of borrowing this explanation.
    refusalDescription: describeTransferRecommendationsRefusal(error),
    isTransferRecommendationsSuccess,
    regenerate,
    refetch
  };
};
